import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserService } from '../services/user.service';
import { Store } from '@ngrx/store';
import { userActions } from './user.actions';
import { ToastService } from '../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UsageTimerService } from '../../shared/services/usageTimer.service';
import { timerActions } from '../../core/store/timer.actions';
import { Router } from '@angular/router';
import { connectionsActions } from '../../connections/store/connections.actions';
import { shoppingCartActions } from '../../shoppingCart/store/shoppingCart.actions';

@Injectable()
export class UserEffects {
  private router = inject(Router);

  constructor(private actions$: Actions,
        private userService: UserService,
        private store: Store,
        private toastService: ToastService,
        private translate: TranslateService,
        private usageTimerService: UsageTimerService) {}

  login$ = createEffect(() => this.actions$.pipe(
      ofType(userActions.login),
      switchMap((action) =>
        this.userService.login(action.user).pipe(
          map(user => {
            if (user.timeout === undefined || user.timeout < 0) {
              this.router.navigate(['/user/timeout']);
              return userActions.logout();
            }
            (window as any).clarity?.('identify', user.id ?? '', { email: user.email });
            this.usageTimerService.setTimeout(user.timeout);
            this.usageTimerService.startTimer();
            this.store.dispatch(timerActions.setStartTime());
            this.store.dispatch(timerActions.updateTimer());
            this.store.dispatch(connectionsActions.clearAllSelectConnection());
            this.store.dispatch(shoppingCartActions.getMyCart());
            return userActions.loginSuccess({user});
          }),
          catchError(() => of(userActions.loginFailure({ error: 'Invalid credentials' })))
        )
      )
  ));

  loginFailure$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.loginFailure),
    tap(() => this.router.navigate(['/user/login']))
  ), { dispatch: false });

  logout$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.logout),
    map((action) => {
      this.userService.logout();
      return userActions.logoutSuccess();
    })
  ));

  timeout$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.logoutTimeout),
    map((action) => {
      this.userService.logout();
      this.router.navigate(['/user/timeout']);
      return userActions.logoutTimeoutSuccess();
    })
  ));

  retryLogin$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.retryLogin),
    switchMap(() =>
      this.userService.retryLogin().pipe(
        map(user => {
          if (user.timeout === undefined || user.timeout < 0) {
            this.router.navigate(['/user/timeout']);
            return userActions.logout();
          }
          this.usageTimerService.setTimeout(user.timeout);
          this.usageTimerService.startTimer();
          this.store.dispatch(timerActions.setStartTime());
          this.store.dispatch(timerActions.updateTimer());
          this.store.dispatch(connectionsActions.clearAllSelectConnection());
          this.store.dispatch(shoppingCartActions.getMyCart());
          return userActions.loginSuccess({user});
        }),
        catchError(() => of(userActions.retryLoginFailure({ error: 'Retry login failed' })))
      )
    )
  ));

  register$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.userRegister),
    switchMap((action) =>
      this.userService.register(action.user).pipe(
        map(_ => {
          this.toastService.Success(this.translate.instant("user.register.registered"), "");
          this.router.navigate(['/user/activate/instructions']);
          return userActions.userRegisterSuccess()
        }),
        catchError(() => of(userActions.loginFailure({ error: 'Invalid data' })))
      )
    )
  ));

  updatePicture$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.updatePicture),
    switchMap((action) =>
      this.userService.changePicture(action.imageData).pipe(
        map((imageUrl) => {
          this.toastService.Success(this.translate.instant("profile.picture.update.success"), "");
          return userActions.updatePictureSuccess(imageUrl);
        }),
        catchError(() => of(userActions.updatePictureFailure('Error updating picture' )))
      )
    )
  ));

  updateGeneralData$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.updateGeneralData),
    switchMap((action) =>
      this.userService.changeGeneralData(action.name, action.email, action.login).pipe(
        map(_ => {
          this.toastService.Success(this.translate.instant("profile.generalData.update.success"), "");
          return userActions.updateGeneralDataSuccess(action.name, action.email, action.login)
        }),
        catchError(() => of(userActions.updateGeneralDataFailure('Error updating user data' )))
      )
    )
  ));

  updateGeneralSettings$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.updateGeneralSettings),
    switchMap((action) =>
      this.userService.changeSettings(action.timeout, action.publicProfile, action.nostrPK).pipe(
        map(_ => {
          this.toastService.Success(this.translate.instant("profile.generalSettings.update.success"), '');
          return userActions.updateGeneralSettingsSuccess(action.timeout, action.publicProfile, action.nostrPK);
        }),
        catchError(() => of(userActions.updateGeneralSettingsFailure('Error updating user general settings' )))
      )
    )
  ));

  updateMyPageSettings$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.updateMyPageSettings),
    switchMap((action) =>
      this.userService.changeMyPageSettings(action.settings).pipe(
        map(_ => {
          this.toastService.Success(this.translate.instant("profile.myPageSettings.updateSuccess"), '');
          return userActions.updateMyPageSettingsSuccess(action.settings.title);
        }),
        catchError(() => of(userActions.updateMyPageSettingsFailure('Error updating my page settings' )))
      )
    )
  ));

  updateProfileBackgroundPicture$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.updateProfileBackgroundPicture),
    switchMap((action) =>
      this.userService.changeProfileBackgroundPicture(action.imageData).pipe(
        map((imageUrl) => {
          this.toastService.Success(this.translate.instant("profile.picture.update.success"), "");
          return userActions.updateProfileBackgroundPictureSuccess(imageUrl);
        }),
        catchError(() => of(userActions.updateProfileBackgroundPictureFailure('Error updating picture' )))
      )
    )
  ));

  registerWaitingList$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.waitingListRegister),
    switchMap((action) =>
      this.userService.registerWaitingList(action.user).pipe(
        tap(() => {
          this.router.navigate(['/user/waitingList']);
        }),
        map(() => userActions.waitingListRegisterSuccess), // You might need some action after navigation
        catchError(() => {
          this.toastService.Error(this.translate.instant("waitingList.error.message"), '');
          return of(userActions.loginFailure({ error: 'Invalid data' }));
        })
      )
    )
  ));

  forgotPassword$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.forgotPassword),
    switchMap(action =>
      this.userService.forgotPassword(action.email, action.language).pipe(
        map(() => {
          this.toastService.Success(
            this.translate.instant('login.resetEmailSent'),
            ''
          );
          return userActions.forgotPasswordSuccess();
        }),
        catchError(() => {
          this.toastService.Error(
            this.translate.instant('login.resetEmailError'),
            ''
          );
          return of(
            userActions.forgotPasswordFailure({
              error: 'Forgot password failed'
            })
          );
        })
      )
    )
  ));

  resendActivation$ = createEffect(() => this.actions$.pipe(
    ofType(userActions.resendActivation),
    switchMap(action =>
      this.userService.resendActivation(action.email).pipe(
        map(() => {
          this.toastService.Success(
            this.translate.instant('login.activationEmailSent'),
            ''
          );
          return userActions.resendActivationSuccess();
        }),
        catchError(() => {
          this.toastService.Error(
            this.translate.instant('login.activationEmailError'),
            ''
          );
          return of(
            userActions.resendActivationFailure({
              error: 'Resend activation failed'
            })
          );
        })
      )
    )
  ));

  loginsso$ = createEffect(() => this.actions$.pipe(
        ofType(userActions.loginWithSso),
        switchMap((action) =>
          this.userService.loginWithSso(action.token).pipe(
            map(user => userActions.loginSuccess({user})),
            catchError(() => of(userActions.loginFailure({ error: 'Invalid credentials' })))
          )
        )
  ));

}
